import styles from "./styles.css";
import formStyles from "../form-styles/styles.css";
import SteppedForms from "../../stepped-form/browser";
import { initComponent } from "../../../utilities/common";
import { AsYouType } from "libphonenumber-js";

export default class Phone {
  constructor(element) {
    this.element = element;
    this.countryCodeDisplay = element.querySelector(
      `.${styles.displayedCountryCode}`
    );
    this.countryDropdown = element.querySelector(`select`);
    const geocountry = document
      ?.querySelector('meta[name="geocountry"]')
      ?.getAttribute("content");
    this.phoneInput = element.querySelector(`[data-tel="national"]`);
    this.phoneCountryCode = element.querySelector('[name="phoneCountryCode"]');
    this.phoneCountryAlpha3 = element.querySelector(
      '[name="phoneCountryAlpha3"]'
    );
    this.phoneCountry = element.querySelector('[name="phoneCountry"]');
    this.countryCodeOutput = element.querySelector("[country-code-output]");
    this.customWhitelist = element.querySelector('[name="customWhitelist"]');
    this.reqForm = element.closest("form");
    this.currentCountry;
    this.malformedMessage = element.dataset.malformed;
    this.requiredMessage = element.dataset.required;
    this.notInWhitelistMessage = element.dataset.notInWhitelist;
    this.validityMessageContainer = this.element.querySelector(
      "[data-custom-message]"
    );
    this.label = this.element.querySelector("label");

    this.phoneInput.addEventListener("blur", () => {
      if (this.label && !this.phoneInput.value) {
        this.countryCodeOutput.classList.remove(styles.countryCodePadding);
      }
      this.checkIsValid(false, false, true);
    });

    this.phoneInput.addEventListener("focus", () => {
      // Adds padding to the countryCode. this can be done in css when the :has selector is fully supported
      if (this.label)
        this.countryCodeOutput.classList.add(styles.countryCodePadding);
    });

    // Set default visible 3 digit country code on page load
    if (this.phoneCountryAlpha3) {
      if (this.phoneCountryAlpha3.getAttribute("value")) {
        this.countryCodeDisplay.textContent =
          this.phoneCountryAlpha3.getAttribute("value");
      } else {
        // A one-time use mutation observer to set the country code when the value is dynamically injected from a wrapping component
        // NOTE: Event listeners can't be used on input type="hidden" which is what phoneCountryAlpha3 is, hence the mutation observer
        const observer = new MutationObserver(
          (mutationsList, observerInstance) => {
            for (const mutation of mutationsList) {
              if (
                mutation.type === "attributes" &&
                mutation.attributeName === "value"
              ) {
                if (
                  mutation.target.getAttribute("value") &&
                  this.countryCodeDisplay
                ) {
                  this.countryCodeDisplay.textContent =
                    mutation.target.getAttribute("value");
                  observerInstance.disconnect();
                  return;
                }
              }
            }
          }
        );
        observer.observe(this.phoneCountryAlpha3, {
          attributes: true,
          attributeFilter: ["value"]
        });
      }
    }
    this.oldInputState = {};
    const getInputState = () => {
      const cursorPosition = this.phoneInput.selectionStart;
      const charsBefore = this.phoneInput.value
        .substring(0, cursorPosition)
        .replace(/[^\d]/g, "");
      const charsAfter = this.phoneInput.value
        .substring(cursorPosition)
        .replace(/[^\d]/g, "");
      const digitIndex = charsBefore.length ? charsBefore.length - 1 : 0;
      return {
        cursorPosition,
        charsBefore,
        charsAfter,
        digitIndex,
        digits: charsBefore + charsAfter
      };
    };

    const setCursorPosition = (originalState) => {
      const str = this.phoneInput.value;
      let digitCount = -1;
      let i = 0;
      for (i; i < str.length && originalState.digitIndex !== digitCount; i++) {
        if (/\d/.test(str[i])) {
          digitCount++;
        }
      }
      const leadingCharException = ~~(
        originalState.digitIndex === 0 &&
        digitCount === 0 &&
        !/\d/.test(this.oldInputState.inputKey) &&
        originalState.charsBefore.length === 0
      );
      const cursorPosition = i + -leadingCharException;
      this.phoneInput.selectionStart = cursorPosition;
      this.phoneInput.selectionEnd = cursorPosition;
    };

    this.phoneInput.addEventListener("keydown", (event) => {
      this.oldInputState.inputKey = event.key;
    });

    this.phoneInput.addEventListener("beforeinput", () => {
      this.oldInputState = getInputState();
    });

    this.phoneInput.addEventListener("input", (event) => {
      // Prevent issues with touch events affecting the cursor logic
      if (!event.key && !event.data) {
        event.preventDefault();
        return;
      }
      const originalState = getInputState();
      const formatter = new AsYouType(this.phoneCountry.value.toUpperCase());
      event.target.value = formatter.input(originalState.digits);
      setCursorPosition(originalState);
    });

    // These functions are called in the Stepped Forms Submit Handler. They are used to check that the input field is valid and to retrieve the phone field input value
    element.getValues = () => this.getValues();
    element.checkIsValid = (asynchronous = true) =>
      this.checkIsValid(asynchronous);
    this.countryDropdown.addEventListener("change", () => {
      this.switchCountry(this.countryDropdown.value, true, false);
    });

    if (geocountry) {
      this.countryDropdown.value = geocountry;
      const newCountryIndex = this.countryDropdown.selectedIndex;
      const newCountryOption = this.countryDropdown.options[newCountryIndex];
      const parent = this.countryDropdown.parentNode;
      if (this.countryCodeDisplay) {
        this.countryCodeDisplay.textContent =
          newCountryOption?.dataset?.phoneCode;
      }
      if (this.phoneCountryCode)
        this.phoneCountryCode.value = newCountryOption?.dataset?.phoneCode;

      const phoneCountry = parent.querySelector('[name="phoneCountry"]');
      if (phoneCountry) phoneCountry.value = newCountryOption?.dataset?.value;

      if (this.phoneCountryAlpha3)
        this.phoneCountryAlpha3.value = newCountryOption?.dataset?.alpha3;

      const customWhitelist = parent.querySelector('[name="customWhitelist"]');
      if (customWhitelist)
        customWhitelist.value = newCountryOption?.hasAttribute("in-whitelist");

      const countryCodeOutput = parent.querySelector("[country-code-output]");
      if (countryCodeOutput)
        countryCodeOutput.innerHTML = newCountryOption?.dataset?.phoneCode;

      const reqForm = parent.closest("form");
      let confirmationPrefComponent = reqForm?.querySelector(
        '[data-type="confirmation-pref"]'
      );
      if (confirmationPrefComponent) {
        confirmationPrefComponent.querySelector(
          '[value="SMS"]'
        ).disabled = true;
        confirmationPrefComponent
          .querySelector('[value="SMS"]')
          .removeAttribute("checked");
        confirmationPrefComponent.querySelector(
          '[value="SMS"]'
        ).checked = false;
        confirmationPrefComponent.querySelector(
          '[value="EMAIL"]'
        ).checked = true;
      }
    } else {
      // Set default 3 digit country code
      this.countryCodeDisplay.textContent = this.phoneCountryAlpha3.value || "";
    }
  }

  phoneValidation(req, resolve, reject, strict = true) {
    let response = req.response;
    this.element.removeAttribute("checking"); // remove the loading animation
    if (!this.phoneInput.required && !this.phoneInput.value) {
      resolve();
    } else if (this.customWhitelist.value === "false") {
      reject(this.notInWhitelistMessage);
    } else if (strict && this.phoneInput.required && !this.phoneInput.value) {
      const externalRequirement = this.element.dataset.conditionalFieldRequired; // This is used by the checkbox-radio, specifically with subscriptions which have custom errors
      reject(externalRequirement || this.requiredMessage);
    } else if (!response.valid) {
      reject(this.malformedMessage);
    } else {
      this.phoneInput.value = response.formatted;
      resolve();
    }
  }

  setValidity(asynchronous, isValid, validationMessage) {
    if (isValid) {
      SteppedForms.setAsValid(this.phoneInput);
      this.phoneInput.setCustomValidity("");
      return asynchronous && Promise.resolve(); // Only return a Promise if it's asynchronous
    } else {
      this.phoneInput.setCustomValidity(validationMessage);
      this.validityMessageContainer.innerText = validationMessage;
      SteppedForms.setAsInvalid(this.phoneInput);
      return asynchronous && Promise.reject();
    }
  }

  processCountryChange() {
    if (this.currentCountry === undefined) {
      const newCountryIndex = this.countryDropdown.selectedIndex;
      const newCountryOption = this.countryDropdown.options[newCountryIndex];
      this.currentCountry = newCountryOption;
    }
    this.phoneCountryCode.value = this.currentCountry.dataset.phoneCode;
    this.phoneCountry.value = this.currentCountry.dataset.value;
    this.phoneCountryAlpha3.value = this.currentCountry.dataset.alpha3;
    this.countryCodeDisplay.textContent = this.currentCountry.dataset.alpha3;
    this.customWhitelist.value =
      this.currentCountry.hasAttribute("in-whitelist");
    let confirmationPrefComponent = this.reqForm.querySelector(
      '[data-type="confirmation-pref"]'
    );
    this.countryCodeOutput.innerHTML = this.phoneCountryCode.value;
    if (confirmationPrefComponent) {
      confirmationPrefComponent
        .querySelector('[value="SMS"]')
        .setAttribute("disabled", "disabled");
      confirmationPrefComponent
        .querySelector('[value="SMS"]')
        .removeAttribute("checked");
      confirmationPrefComponent.querySelector('[value="SMS"]').checked = false;
      confirmationPrefComponent.querySelector('[value="EMAIL"]').checked = true;
    }
  }

  switchCountry(countryCode, validate, strict = true) {
    if (countryCode) this.countryDropdown.value = countryCode;
    const newCountryIndex = this.countryDropdown.selectedIndex;
    const newCountryOption = this.countryDropdown.options[newCountryIndex];
    this.currentCountry = newCountryOption;
    this.countryCodeDisplay.textContent = this.currentCountry.dataset.alpha3;
    // when the page initially loads, there is no need to validate the phone field, but there is need to process the country code
    if (validate) {
      this.checkIsValid(false, strict, true);
    }
    this.processCountryChange();
  }

  dispatchValidationEvent(valid) {
    this.phoneInput.dispatchEvent(
      new CustomEvent("field-validated", {
        bubbles: true,
        detail: { fieldType: "phone", valid }
      })
    );
  }

  getValues = () => {
    const value = this.phoneCountryCode.value + " " + this.phoneInput.value;
    return value && value !== "" ? [value] : [];
  };

  checkIsValid = (asynchronous = true, strict = true, logAnalytics = false) => {
    // Only validate with API if there is a value
    if (!this.phoneInput.value) {
      // If strict (form submit) and required, handle required validation
      if (strict && this.phoneInput.required) {
        return this.setValidity(asynchronous, false, this.requiredMessage);
      }
      // Otherwise, treat as valid (empty and not required)
      return this.setValidity(asynchronous, true);
    }

    return new Promise((resolve, reject) => {
      this.processCountryChange();
      this.element.setAttribute("checking", ""); // will cause a loading animation to be injected
      this.phoneInput.classList.remove(formStyles.invalid);
      this.phoneInput.classList.remove(formStyles.valid);
      const req = new XMLHttpRequest();
      req.addEventListener("load", () => {
        this.phoneValidation(req, resolve, reject, strict);
      });
      let param = encodeURIComponent(
        `${this.phoneCountryCode.value} ${this.phoneInput.value}`
      );
      req.open(
        "POST",
        `${window.PUBLIC_ENV.CONSOLIDATION_PREFIX}/api/phoneVerify?phone=${param}&country=${this.currentCountry.dataset.value}`
      );
      req.responseType = "json";
      req.send();
    }).then(
      () => {
        this.setValidity(asynchronous, true);
        if (logAnalytics) this.dispatchValidationEvent(true);
      },
      (error) => {
        this.setValidity(asynchronous, false, error);
        if (logAnalytics) this.dispatchValidationEvent(false);
      }
    );
  };
}

export const init = () => {
  initComponent("phone-field", (element) => new Phone(element));
};
init();
